/**
 * Data Access Layer for People Participants
 * Staff assignments to roles for people (artists)
 */

import { getClient } from './client.js';
import { TABLES } from './tables.js';
import { getCurrentAccountId } from './account-context.js';
import type { PeopleParticipant, PeopleParticipantCreate } from './types.js';

/**
 * List all participants for a person
 */
export async function listPeopleParticipants(personId: string): Promise<PeopleParticipant[]> {
  const { data, error } = await getClient()
    .from(TABLES.people_participants)
    .select(`
      *,
      users:user_id (name, email),
      assigned_people:assigned_person_id (name)
    `)
    .eq('person_id', personId)
    .order('role');

  if (error) throw error;

  // Map the joined data to flat structure
  return (data || []).map((row: any) => ({
    id: row.id,
    person_id: row.person_id,
    user_id: row.user_id,
    assigned_person_id: row.assigned_person_id,
    role: row.role,
    account_id: row.account_id,
    metadata: row.metadata || {},
    created_at: row.created_at,
    updated_at: row.updated_at,
    user_name: row.users?.name || undefined,
    user_email: row.users?.email || undefined,
    assigned_person_name: row.assigned_people?.name || undefined,
  })) as PeopleParticipant[];
}

/**
 * Get participants for multiple people in a batch
 */
export async function listPeopleParticipantsBatch(personIds: string[]): Promise<PeopleParticipant[]> {
  if (personIds.length === 0) return [];

  const { data, error } = await getClient()
    .from(TABLES.people_participants)
    .select(`
      *,
      users:user_id (name, email),
      assigned_people:assigned_person_id (name)
    `)
    .in('person_id', personIds)
    .order('role');

  if (error) throw error;

  return (data || []).map((row: any) => ({
    id: row.id,
    person_id: row.person_id,
    user_id: row.user_id,
    assigned_person_id: row.assigned_person_id,
    role: row.role,
    account_id: row.account_id,
    metadata: row.metadata || {},
    created_at: row.created_at,
    updated_at: row.updated_at,
    user_name: row.users?.name || undefined,
    user_email: row.users?.email || undefined,
    assigned_person_name: row.assigned_people?.name || undefined,
  })) as PeopleParticipant[];
}

/**
 * Add a participant to a person
 */
export async function addPeopleParticipant(input: PeopleParticipantCreate): Promise<PeopleParticipant> {
  const { data, error } = await getClient()
    .from(TABLES.people_participants)
    .insert({
      person_id: input.person_id,
      user_id: input.user_id || null,
      assigned_person_id: input.assigned_person_id || null,
      role: input.role,
      account_id: input.account_id,
      metadata: input.metadata || {},
    })
    .select(`
      *,
      users:user_id (name, email),
      assigned_people:assigned_person_id (name)
    `)
    .single();

  if (error) throw error;

  const row = data as any;
  return {
    id: row.id,
    person_id: row.person_id,
    user_id: row.user_id,
    assigned_person_id: row.assigned_person_id,
    role: row.role,
    account_id: row.account_id,
    metadata: row.metadata || {},
    created_at: row.created_at,
    updated_at: row.updated_at,
    user_name: row.users?.name || undefined,
    user_email: row.users?.email || undefined,
    assigned_person_name: row.assigned_people?.name || undefined,
  } as PeopleParticipant;
}

/**
 * Remove a participant by ID
 */
export async function removePeopleParticipant(id: string): Promise<void> {
  const { error } = await getClient()
    .from(TABLES.people_participants)
    .delete()
    .eq('id', id);

  if (error) throw error;
}

/**
 * Remove all participants for a person
 */
export async function removeAllPeopleParticipants(personId: string): Promise<void> {
  const { error } = await getClient()
    .from(TABLES.people_participants)
    .delete()
    .eq('person_id', personId);

  if (error) throw error;
}

/**
 * Get participants by role for a person
 */
export async function getPeopleParticipantsByRole(personId: string, role: string): Promise<PeopleParticipant[]> {
  const { data, error } = await getClient()
    .from(TABLES.people_participants)
    .select(`
      *,
      users:user_id (name, email),
      assigned_people:assigned_person_id (name)
    `)
    .eq('person_id', personId)
    .eq('role', role);

  if (error) throw error;

  return (data || []).map((row: any) => ({
    id: row.id,
    person_id: row.person_id,
    user_id: row.user_id,
    assigned_person_id: row.assigned_person_id,
    role: row.role,
    account_id: row.account_id,
    metadata: row.metadata || {},
    created_at: row.created_at,
    updated_at: row.updated_at,
    user_name: row.users?.name || undefined,
    user_email: row.users?.email || undefined,
    assigned_person_name: row.assigned_people?.name || undefined,
  })) as PeopleParticipant[];
}

/**
 * Get participants by roles for a person (multiple roles)
 */
export async function getPeopleParticipantsByRoles(personId: string, roles: string[]): Promise<PeopleParticipant[]> {
  if (roles.length === 0) return [];

  const { data, error } = await getClient()
    .from(TABLES.people_participants)
    .select(`
      *,
      users:user_id (name, email),
      assigned_people:assigned_person_id (name)
    `)
    .eq('person_id', personId)
    .in('role', roles);

  if (error) throw error;

  return (data || []).map((row: any) => ({
    id: row.id,
    person_id: row.person_id,
    user_id: row.user_id,
    assigned_person_id: row.assigned_person_id,
    role: row.role,
    account_id: row.account_id,
    metadata: row.metadata || {},
    created_at: row.created_at,
    updated_at: row.updated_at,
    user_name: row.users?.name || undefined,
    user_email: row.users?.email || undefined,
    assigned_person_name: row.assigned_people?.name || undefined,
  })) as PeopleParticipant[];
}
